import { AppShell, Box, Divider, NavLink, ScrollArea, Stack, Text } from '@mantine/core';
import {
  IconAlertCircle,
  IconBolt,
  IconBox,
  IconBulb,
  IconCode,
  IconEye,
  IconFiles,
  IconFlask,
  IconHistory,
  IconLink,
  IconUser,
  IconUserCircle,
  IconUsers,
} from '@tabler/icons-react';
/**
 * SettingsPage — vertical NavLink-style menu with grouped sections.
 *
 *   ACCOUNT     · My account
 *   LIBRARIES   · Angles / Avatars / Problem mechanisms / Solution mechanisms /
 *                 Ingredients / Awareness levels
 *   ADMIN       · Users / Products / Audit log         (admin+)
 *   SYSTEM      · Source pack / System prompts         (system_owner)
 *
 * The previous flat Mantine Tabs.List had no visual separation between
 * the four functional groups — they read as one long list. This rewrite
 * uses NavLink + group headers so the structure is obvious at a glance.
 */
import { useState } from 'react';
import { useAuthStore } from '../state/auth-store';
import AuditLogTab from './settings/AuditLogTab';
import AnglesTab from './settings/LibrariesTabs/AnglesTab';
import AvatarsTab from './settings/LibrariesTabs/AvatarsTab';
import AwarenessLevelsTab from './settings/LibrariesTabs/AwarenessLevelsTab';
import IngredientsTab from './settings/LibrariesTabs/IngredientsTab';
import LandingPageUrlsTab from './settings/LibrariesTabs/LandingPageUrlsTab';
import ProblemMechanismsTab from './settings/LibrariesTabs/ProblemMechanismsTab';
import SolutionMechanismsTab from './settings/LibrariesTabs/SolutionMechanismsTab';
import MyAccountTab from './settings/MyAccountTab';
import ProductsTab from './settings/ProductsTab';
import SourcePackTab from './settings/SourcePackTab';
import SystemPromptsTab from './settings/SystemPromptsTab';
import UsersTab from './settings/UsersTab';

// ─── Role helpers ─────────────────────────────────────────────────────────────

type Role = 'writer' | 'admin' | 'system_owner';
const isAdmin = (r: Role) => r === 'admin' || r === 'system_owner';
const isSystemOwner = (r: Role) => r === 'system_owner';

// ─── SectionLabel ─────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text
      tt="uppercase"
      fz="xs"
      fw={700}
      c="dimmed"
      px="sm"
      pt="sm"
      pb={4}
      style={{ letterSpacing: '0.06em' }}
    >
      {children}
    </Text>
  );
}

// ─── SettingsPage ─────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const [active, setActive] = useState<string>('my-account');

  if (!user) return null;

  const role = user.role as Role;
  const adminPlus = isAdmin(role);
  const systemOwner = isSystemOwner(role);

  const awarenessCanWrite = adminPlus;
  const libraryCanWrite = true;

  type Item = {
    key: string;
    label: string;
    icon: React.ReactNode;
    render: () => React.ReactNode;
  };
  type Section = { label: string; items: Item[] };

  const sections: Section[] = [
    {
      label: 'Account',
      items: [
        {
          key: 'my-account',
          label: 'My account',
          icon: <IconUserCircle size={16} />,
          render: () => <MyAccountTab />,
        },
      ],
    },
    {
      label: 'Libraries',
      items: [
        {
          key: 'angles',
          label: 'Angles',
          icon: <IconBulb size={16} />,
          render: () => <AnglesTab canWrite={libraryCanWrite} />,
        },
        {
          key: 'avatars',
          label: 'Avatars',
          icon: <IconUser size={16} />,
          render: () => <AvatarsTab canWrite={libraryCanWrite} />,
        },
        {
          key: 'problem-mechanisms',
          label: 'Problem mechanisms',
          icon: <IconAlertCircle size={16} />,
          render: () => <ProblemMechanismsTab canWrite={libraryCanWrite} />,
        },
        {
          key: 'solution-mechanisms',
          label: 'Solution mechanisms',
          icon: <IconBolt size={16} />,
          render: () => <SolutionMechanismsTab canWrite={libraryCanWrite} />,
        },
        {
          key: 'ingredients',
          label: 'Ingredients',
          icon: <IconFlask size={16} />,
          render: () => <IngredientsTab canWrite={libraryCanWrite} />,
        },
        {
          key: 'awareness-levels',
          label: 'Awareness levels',
          icon: <IconEye size={16} />,
          render: () => <AwarenessLevelsTab canWrite={awarenessCanWrite} />,
        },
        {
          key: 'landing-page-urls',
          label: 'Landing page URLs',
          icon: <IconLink size={16} />,
          render: () => <LandingPageUrlsTab canWrite={libraryCanWrite} />,
        },
      ],
    },
  ];

  if (adminPlus) {
    sections.push({
      label: 'Administration',
      items: [
        {
          key: 'users',
          label: 'Users',
          icon: <IconUsers size={16} />,
          render: () => <UsersTab />,
        },
        {
          key: 'products',
          label: 'Products',
          icon: <IconBox size={16} />,
          render: () => <ProductsTab />,
        },
        {
          key: 'audit-log',
          label: 'Audit log',
          icon: <IconHistory size={16} />,
          render: () => <AuditLogTab />,
        },
      ],
    });
  }

  if (systemOwner) {
    sections.push({
      label: 'System',
      items: [
        {
          key: 'source-pack',
          label: 'Source pack',
          icon: <IconFiles size={16} />,
          render: () => <SourcePackTab />,
        },
        {
          key: 'system-prompts',
          label: 'System prompts',
          icon: <IconCode size={16} />,
          render: () => <SystemPromptsTab />,
        },
      ],
    });
  }

  const allItems = sections.flatMap((s) => s.items);
  const activeItem = allItems.find((i) => i.key === active) ?? allItems[0];
  if (!activeItem) return null;

  return (
    <AppShell.Main data-testid="settings-page" p={0}>
      <Box
        style={{
          display: 'flex',
          height: '100%',
          minHeight: 'calc(100vh - 56px)',
        }}
      >
        {/* ── Left navigation ────────────────────────────────────────────── */}
        <ScrollArea
          h="100%"
          w={240}
          style={{
            flexShrink: 0,
            borderRight: '1px solid var(--mantine-color-default-border)',
            background: 'var(--mantine-color-gray-0)',
          }}
        >
          <Stack gap={0} py="xs">
            {sections.map((section, idx) => (
              <Box key={section.label}>
                {idx > 0 && <Divider my="xs" />}
                <SectionLabel>{section.label}</SectionLabel>
                {section.items.map((item) => (
                  <NavLink
                    key={item.key}
                    label={item.label}
                    leftSection={item.icon}
                    active={active === item.key}
                    onClick={() => setActive(item.key)}
                    data-testid={`tab-${item.key}`}
                    color="accent"
                    styles={{
                      root: {
                        borderRadius: 0,
                        paddingLeft: 14,
                        paddingRight: 14,
                      },
                      label: { fontSize: 14 },
                    }}
                  />
                ))}
              </Box>
            ))}
          </Stack>
        </ScrollArea>

        {/* ── Active panel ───────────────────────────────────────────────── */}
        <Box style={{ flex: 1, overflow: 'auto', padding: 24 }}>{activeItem.render()}</Box>
      </Box>
    </AppShell.Main>
  );
}
